import { useMemo } from "react";
import { EMPLOYEE_SCALE, REVENUE_SCALE } from "../../../helper/rangeFilter";
import { FILTER_IDS } from "./filterIds";
import { summarizeRange, summarizeSelection, summarizeText } from "./filterSummary";

function useFilterSummaries({
    termsInclude, termsExclude, industryInclude, industryExclude,
    locations, revenueLowerBound, revenueUpperBound, employeesLowerBound,
    employeesUpperBound, naturalQuery, selectedSavedSearch
}) {
const termsSummary = useMemo(
    () => summarizeSelection("Terms", termsInclude, termsExclude),
    [termsInclude, termsExclude]
  );

const industrySummary = useMemo(
    () => summarizeSelection("Industry", industryInclude, industryExclude),
    [industryInclude, industryExclude]
  );

const locationSummary = useMemo(
    () => summarizeSelection("Location", locations),
    [locations]
  );

const revenueSummary = useMemo(
    () => summarizeRange("Revenue", revenueLowerBound, revenueUpperBound, REVENUE_SCALE),
    [revenueLowerBound, revenueUpperBound]
  );

const employeeSummary = useMemo(
    () => summarizeRange("Employee", employeesLowerBound, employeesUpperBound, EMPLOYEE_SCALE),
    [employeesLowerBound, employeesUpperBound]
  );

const naturalLanguageSummary = useMemo(() => {
    const summary = summarizeText("Natural Language", naturalQuery);
    if (!summary.isActive) {
      return { ...summary, label: "Natural Language", displayLabel: "Natural Language" };
    }
    return summary;
  }, [naturalQuery]);

const savedSearchesSummary = useMemo(() => {
    if (!selectedSavedSearch?.name) {
      return {
        label: "Saved Searches",
        displayLabel: "Saved Searches",
        valueLabel: "",
        isActive: false,
        count: 0,
        variant: "empty",
      };
    }
    const name = String(selectedSavedSearch.name).trim();
    return {
      label: `Saved Searches: ${name}`,
      displayLabel: name.length > 15 ? `${name.slice(0, 14)}\u2026` : name,
      valueLabel: name,
      isActive: true,
      count: 1,
      variant: "single",
    };
  }, [selectedSavedSearch]);

const summaries = useMemo(
    () => ({
      [FILTER_IDS.TERMS]: termsSummary,
      [FILTER_IDS.INDUSTRY]: industrySummary,
      [FILTER_IDS.LOCATION]: locationSummary,
      [FILTER_IDS.REVENUE]: revenueSummary,
      [FILTER_IDS.EMPLOYEE]: employeeSummary,
      [FILTER_IDS.NATURAL_LANGUAGE]: naturalLanguageSummary,
      [FILTER_IDS.SAVED_SEARCHES]: savedSearchesSummary,
    }),
    [
      termsSummary,
      industrySummary,
      locationSummary,
      revenueSummary,
      employeeSummary,
      naturalLanguageSummary,
      savedSearchesSummary,
    ]
  );

return {
    revenueSummary, employeeSummary, summaries
};
}

export { useFilterSummaries };
